import Logger from './logger';
import DefaultsParam from './defaults/input';
import Language from './defaults/language';

export default class FW {

  static #getScrollLeft() {
    if (typeof pageXOffset === 'number') {
      return pageXOffset;
    } else {
      if (document.documentElement &&
        typeof document.documentElement.scrollLeft === 'number' &&
        document.documentElement.scrollLeft > 0) {
        return document.documentElement.scrollLeft;
      } else if (document.body && typeof document.body.scrollLeft === 'number' && document.body.scrollLeft > 0) {
        return document.body.scrollLeft;
      }
    }
    return 0;
  }

  static #getScrollTop() {
    if (typeof pageYOffset === 'number') {
      return pageYOffset;
    } else {
      if (document.documentElement &&
        typeof document.documentElement.scrollTop === 'number' &&
        document.documentElement.scrollTop > 0) {
        return document.documentElement.scrollTop;
      } else {
        if (document.body && typeof document.body.scrollTop === 'number' && document.body.scrollTop > 0) {
          return document.body.scrollTop;
        }
      }
    }
    return 0;
  }

  static #getComputedStyle(element, style) {
    let propertyValue = '';
    if (element && typeof getComputedStyle !== 'undefined') {
      const cs = getComputedStyle(element, null);
      if (cs) {
        propertyValue = cs.getPropertyValue(style);
        propertyValue = propertyValue.toString().toLowerCase();
      }
    }
    return propertyValue;
  }

  static #getStyleAttributeData(element, style) {
    let styleAttributeData = FW.#getComputedStyle(element, style);
    styleAttributeData = styleAttributeData.toString();
    if (styleAttributeData.indexOf('px') > -1) {
      styleAttributeData = styleAttributeData.replace('px', '');
    }
    return parseFloat(styleAttributeData);
  }

  static #appendToHead(data, shadowRoot) {
    try {
      let head;
      if (shadowRoot) {
        head = shadowRoot;
      } else {
        head = document.head;
      }
      if (!head) {
        head = document.body;
      }
      if (head && data) {
        head.appendChild(data);
      }
    } catch (warning) {
      console.warn(warning);
    }
  }

  static #b64EncodeUnicode(str) {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode('0x' + p1);
    }));
  }

  static #trimStr(str, length) {
    if (str.length > length) {
      return str.substring(0, length);
    }
    return str;
  }

  static #makeStrId(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  static #arrayToString(array) {
    const uint16array = new Uint16Array(array.buffer);
    return String.fromCharCode.apply(null, uint16array);
  }

  static get blackFrame() {
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=';
  }

  static getPosX(event) {
    if (event) {
      if (typeof event.pageX === 'number') {
        return event.pageX;
      } else if (typeof event.clientX === 'number') {
        return event.clientX + FW.#getScrollLeft();
      }
    }
    return 0;
  }

  static getPosXTouch(event) {
    if (event && event.changedTouches && event.changedTouches[0]) {
      if (FW.isFiniteNumber(event.changedTouches[0].pageX)) {
        return event.changedTouches[0].pageX;
      }
    }
    return 0;
  }

  static getPosY(event) {
    if (event) {
      if (typeof event.pageY === 'number') {
        return event.pageY;
      } else if (typeof event.clientY === 'number') {
        return event.clientY + FW.#getScrollTop();
      }
    }
    return 0;
  }

  static getPosYTouch(event) {
    if (event && event.changedTouches && event.changedTouches[0]) {
      if (FW.isFiniteNumber(event.changedTouches[0].pageY)) {
        return event.changedTouches[0].pageY;
      }
    }
    return 0;
  }

  static isFiniteNumber(n) {
    if (typeof n === 'number' && Number.isFinite(n) && !Number.isNaN(n)) {
      return true;
    }
    return false;
  }

  static isInfinityNumber(n) {
    if (typeof n === 'number' && !Number.isFinite(n) && !Number.isNaN(n)) {
      return true;
    }
    return false;
  }

  static getPrecisionWidthWithPaddingAndBorder(element) {
    if (element) {
      const rect = element.getBoundingClientRect();
      if (rect && rect.width) {
        return rect.width;
      }
    }
    return 0;
  }

  static getPrecisionWidthNoPaddingNoBorder(element) {
    let trueWidth = 0;
    if (element) {
      trueWidth = FW.getPrecisionWidthWithPaddingAndBorder(element);
      if (trueWidth > 0) {
        let toRemove = 0;
        const paddingLeft = FW.#getStyleAttributeData(element, 'padding-left');
        if (paddingLeft > 0) {
          toRemove += paddingLeft;
        }
        const paddingRight = FW.#getStyleAttributeData(element, 'padding-right');
        if (paddingRight > 0) {
          toRemove += paddingRight;
        }
        const borderLeft = FW.#getStyleAttributeData(element, 'border-left-width');
        if (borderLeft > 0) {
          toRemove += borderLeft;
        }
        const borderRight = FW.#getStyleAttributeData(element, 'border-right-width');
        if (borderRight > 0) {
          toRemove += borderRight;
        }
        trueWidth = trueWidth - toRemove;
      }
    }
    return trueWidth;
  }

  static getPrecisionHeightWithPaddingAndBorder(element) {
    if (element) {
      const rect = element.getBoundingClientRect();
      if (rect && rect.height) {
        return rect.height;
      }
    }
    return 0;
  }

  static getPrecisionHeightNoPaddingNoBorder(element) {
    let trueHeight = 0;
    if (element) {
      trueHeight = FW.getPrecisionHeightWithPaddingAndBorder(element);
      if (trueHeight > 0) {
        let toRemove = 0;
        const paddingTop = FW.#getStyleAttributeData(element, 'padding-top');
        if (paddingTop > 0) {
          toRemove += paddingTop;
        }
        const paddingBottom = FW.#getStyleAttributeData(element, 'padding-bottom');
        if (paddingBottom > 0) {
          toRemove += paddingBottom;
        }
        const borderTop = FW.#getStyleAttributeData(element, 'border-top-width');
        if (borderTop > 0) {
          toRemove += borderTop;
        }
        const borderBottom = FW.#getStyleAttributeData(element, 'border-bottom-width');
        if (borderBottom > 0) {
          toRemove += borderBottom;
        }
        trueHeight = trueHeight - toRemove;
      }
    }
    return trueHeight;
  }

  static getAbsLeft(element) {
    if (element) {
      const rect = element.getBoundingClientRect();
      return (rect.left + FW.#getScrollLeft()) || 0;
    }
    return 0;
  }

  static getAbsTop(element) {
    if (element) {
      const rect = element.getBoundingClientRect();
      return (rect.top + FW.#getScrollTop()) || 0;
    }
    return 0;
  }

  static setText(element, text) {
    if (typeof element !== 'undefined' && typeof text === 'string') {
      element.textContent = text;
    }
  }

  static removeElement(element) {
    if (element && element.parentNode) {
      try {
        element.parentNode.removeChild(element);
      } catch (warning) {
        console.warn(warning);
      }
    }
  }

  static emptyElement(element) {
    while (element.firstElementChild) {
      element.firstElementChild.remove();
    }
  }

  static createSyntheticEvent(eventName, element) {
    if (element && typeof Event === 'function') {
      const event = new Event(eventName);
      element.dispatchEvent(event);
      Logger.print('synthetic-event', eventName);
    }
  }

  static isVisible(element) {
    if (element && element.style.visibility === 'visible') {
      return true;
    }
    return false;
  }

  static show(element) {
    if (element) {
      element.style.display = 'block';
    }
  }

  static showFlex(element) {
    if (element) {
      element.style.display = 'flex';
    }
  }

  static showInline(element) {
    if (element) {
      element.style.display = 'inline';
    }
  }

  static hide(element) {
    if (element) {
      element.style.display = 'none';
    }
  }

  static fadeIn(element, immediate) {
    if (element) {
      if (element.style.display === 'none') {
        element.style.display = 'block';
      }
      if (immediate) {
        element.style.transition = 'unset';
      } else {
        element.style.transition = 'opacity 0.4s ease-in, visibility 0.4s ease-in';
      }
      element.style.opacity = '1';
      element.style.visibility = 'visible';
    }
  }

  static fadeOut(element, immediate) {
    if (element) {
      if (immediate) {
        element.style.display = 'none';
      }
      element.style.opacity = '0';
      element.style.visibility = 'hidden';
    }
  }

  static readableTime(time) {
    if (typeof time === 'number' && time >= 0) {
      let seconds = Math.floor(time % 60);
      let minutes = Math.floor(time * 1.0 / 60);
      let hours = 0;
      let secondString = '';
      let minutesString = '';
      let hoursString = '';
      if (minutes > 59) {
        hours = Math.floor(time * 1.0 / 3600);
        minutes = Math.floor(((time * 1.0 / 3600) % 1) * 60);
        seconds = Math.floor(time % 60);
      }
      if (seconds < 10) {
        secondString = '0' + seconds.toString();
      } else {
        secondString = seconds.toString();
      }
      if (minutes < 10) {
        minutesString = '0' + minutes.toString();
      } else {
        minutesString = minutes.toString();
      }
      if (hours > 0) {
        hoursString = hours + ':';
      } else if (hours === 0) {
        hoursString = '';
      }
      return hoursString + minutesString + ':' + secondString;
    }
    return '';
  }

  static dateToSeconds(date) {
    if (date) {
      const re1 = /(\d+:)?(\d+):(\d\d?)\.?(\d+)?/;
      const dateArray = date.match(re1);
      if (dateArray) {
        const dateArrayLength = dateArray.length;
        if (dateArrayLength > 1) {
          dateArray.shift();
          let time = 0;
          if (dateArray[0]) {
            let hourString = dateArray[0];
            if (hourString.indexOf(':') > -1) {
              hourString = hourString.replace(':', '');
            }
            time += parseInt(hourString) * 60 * 60;
          }
          if (dateArray[1]) {
            time += parseInt(dateArray[1]) * 60;
          }
          if (dateArray[2]) {
            time += parseInt(dateArray[2]);
          }
          if (dateArray[3]) {
            time += parseInt(dateArray[3]) / 1000;
          }
          return time;
        }
      }
    }
    return 0;
  }

  static filterInitialQuality(initialQuality, src) {
    if (initialQuality < 0 || typeof src[initialQuality] !== 'string') {
      return 0;
    } else {
      return initialQuality;
    }
  }

  static isEmptyObject(obj) {
    if (typeof obj === 'object' && obj !== null && Object.keys(obj).length === 0) {
      return true;
    }
    return false;
  }

  static ajax(url, withCredentials, requestType) {
    return new Promise((resolve, reject) => {
      if (typeof XMLHttpRequest !== 'undefined') {
        const xhr = new XMLHttpRequest();
        xhr.open(requestType, url, true);
        xhr.timeout = 10000;
        if (withCredentials) {
          xhr.withCredentials = true;
        }
        xhr.onloadend = () => {
          if (typeof xhr.status === 'number' && xhr.status >= 200 && xhr.status < 300) {
            if (requestType === 'HEAD') {
              resolve('');
            } else {
              if (typeof xhr.responseText === 'string' && xhr.responseText !== '') {
                resolve(xhr.responseText);
              } else {
                reject();
              }
            }
          } else {
            reject();
          }
        };
        xhr.ontimeout = () => {
          Logger.print('info', 'XMLHttpRequest timeout');
          reject();
        };
        xhr.send(null);
      } else {
        reject();
      }
    });
  }

  static getNow() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    } else if (typeof Date !== 'undefined') {
      return Date.now();
    }
    return 0;
  }

  static getRandomInt(min, max) {
    const minus = Math.ceil(min);
    const maxus = Math.floor(max);
    return Math.floor(Math.random() * (maxus - minus)) + minus; //The maximum is exclusive and the minimum is inclusive
  }

  static cssPropertySupport(property) {
    const div = document.createElement('div');
    if (typeof div.style[property] !== 'undefined') {
      return true;
    }
    return false;
  }

  static stopPropagation(event) {
    if (event) {
      event.stopPropagation();
    }
  }

  static contextMenuInteraction(event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  static appendStyle(css, shadowRoot) {
    if (css) {
      const style = document.createElement('style');
      style.appendChild(document.createTextNode(css));
      FW.#appendToHead(style, shadowRoot);
    }
  }

  static getScript(src, load, error, shadowRoot) {
    if (typeof src === 'string' && typeof load === 'function' && typeof error === 'function') {
      const script = document.createElement('script');
      if (typeof script.async !== 'undefined') {
        script.async = true;
      }
      script.onload = load;
      script.onerror = error;
      script.src = src;
      FW.#appendToHead(script, shadowRoot);
    }
  }

  static getMediaCurrentTime(media) {
    if (media && typeof media.currentTime !== 'undefined' && FW.isFiniteNumber(media.currentTime) && media.currentTime >= 0) {
      return media.currentTime;
    }
    return -1;
  }

  static getMediaDuration(media) {
    if (media && typeof media.duration !== 'undefined' && FW.isFiniteNumber(media.duration) && media.duration >= 0) {
      return media.duration;
    }
    return -1;
  }

  static clearNativeHtml5Buffer(media) {
    // we need call load after src removal 
    // see https://dev.w3.org/html5/spec-author-view/video.html#best-practices-for-authors-using-media-elements
    try {
      if (media) {
        media.removeAttribute('src');
        media.load();
      }
    } catch (warning) {
      console.warn(warning);
    }
  }

  static createElement(elementType, className, textContent, rtl, textContentInSpan) {
    const element = document.createElement(elementType);
    if (typeof className === 'string' && className !== '') {
      if (rtl) {
        element.setAttribute('dir', 'rtl');
      }
      element.className = className;
    } else if (className === null && rtl) {
      element.setAttribute('dir', 'rtl');
    }
    if (typeof textContent === 'string') {
      if (textContentInSpan) {
        const span = document.createElement('span');
        span.textContent = textContent;
        element.appendChild(span);
      } else {
        element.textContent = textContent;
      }
    }
    return element;
  }

  static precisionRound(number, digit) {
    const precision = Math.pow(10, digit);
    return Math.round(number * precision) / precision;
  }

  static getClientWidth(element) {
    if (element && typeof element.clientWidth === 'number' && element.clientWidth > 0) {
      return element.clientWidth;
    }
    return 0;
  }

  static getPoster(posters) {
    let ratio = window.devicePixelRatio || 1;
    const filteredPostersPerRatio = posters.filter((poster, index) => {
      return index + 1 >= ratio;
    });
    if (filteredPostersPerRatio.length === 0) {
      return posters[posters.length - 1];
    } else {
      return filteredPostersPerRatio[0];
    }
  }

  static filterContentMetadata(inputMetadata) {
    // fill with default values 
    const defaultsContentMetadata = DefaultsParam.contentMetadata;
    let defaultKeys = Object.keys(defaultsContentMetadata);
    let metadata = inputMetadata;
    if (!inputMetadata) {
      metadata = defaultsContentMetadata;
    }
    defaultKeys.forEach(defaultKey => {
      if (typeof metadata[defaultKey] === 'undefined' ||
        (typeof metadata[defaultKey] !== typeof defaultsContentMetadata[defaultKey])) {
        metadata[defaultKey] = defaultsContentMetadata[defaultKey];
      }
    });
    // validate input values
    let keys = Object.keys(metadata);
    for (let i = 0, len = keys.length; i < len; i++) {
      let key = keys[i];
      switch (key) {
        case 'title':
        case 'description':
        case 'artist':
        case 'album':
        case 'id':
        case 'releaseDate':
        case 'thumbnail':
        case 'duration':
        case 'googleCastType':
          if (typeof metadata[key] !== 'string') {
            metadata[key] = '';
          }
          break;
        case 'artwork':
          if (!Array.isArray(metadata[key])) {
            metadata[key] = [];
          }
          break;
        case 'poster':
          if (!Array.isArray(metadata[key])) {
            metadata[key] = [];
          }
          break;
        case 'endOfVideoPoster':
          if (!Array.isArray(metadata[key])) {
            metadata[key] = [];
          }
          break;
        default:
          break;
      }
    }
    // if no artwork but poster - use poster
    if (metadata.artwork.length === 0 && metadata.poster.length > 0) {
      for (let i = 0, len = metadata.poster.length; i < len; i++) {
        metadata.artwork[i] = {};
        metadata.artwork[i].src = metadata.poster[i];
      }
    }
    return metadata;
  }

  static getCMCDContentId(id) {
    if (id) {
      return FW.#trimStr(FW.#b64EncodeUnicode(id));
    } else {
      return FW.#makeStrId(32);
    }
  }

  static generateUuid() {
    const url = URL.createObjectURL(new Blob());
    const uuid = url.toString();
    URL.revokeObjectURL(url);
    return uuid.substr(uuid.lastIndexOf('/') + 1);
  }

  // Do not put this in fps.js
  // Do not modify this function - do not use => in place of function
  static waitForEvent(name, action, target) {
    target.addEventListener(name, function () {
      action(arguments[0]);
    });
  }

  static #base64EncodeUint8Array(input) {
    const keyStr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output = '';
    let chr1, chr2, chr3, enc1, enc2, enc3, enc4;
    let i = 0;
    while (i < input.length) {
      chr1 = input[i++];
      chr2 = i < input.length ? input[i++] : Number.NaN; // Not sure if the index
      chr3 = i < input.length ? input[i++] : Number.NaN; // checks are needed here
      enc1 = chr1 >> 2;
      enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
      enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
      enc4 = chr3 & 63;
      if (Number.isNaN(chr2)) {
        enc3 = enc4 = 64;
      } else if (Number.isNaN(chr3)) {
        enc4 = 64;
      }

      output += keyStr.charAt(enc1) + keyStr.charAt(enc2) + keyStr.charAt(enc3) + keyStr.charAt(enc4);
    }
    return output;
  }

  static #base64DecodeUint8Array(input) {
    let raw;
    try {
      raw = atob(input);
    } catch (warning) {
      console.warn(warning);
      return new Uint8Array(0);
    }
    const rawLength = raw.length;
    const array = new Uint8Array(new ArrayBuffer(rawLength));
    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  }

  static defaultExtractContentId(initData) {
    Logger.print('fps', 'using default extractContentId');
    const contentId = FW.#arrayToString(initData);
    // contentId is passed up as a URI, from which the host must be extracted:
    const link = document.createElement('a');
    link.href = contentId;
    return link.hostname;
  }

  static defaultLicenseRequestMessage(message, session) {
    return 'spc=' + FW.#base64EncodeUint8Array(message) + '&assetId=' + encodeURIComponent(session.contentId);
  }

  static defaultLicenseRequestLoaded(event) {
    Logger.print('fps', 'using default licenseRequestLoaded');
    const request = event.target;
    const session = request.session;
    // response can be of the form: '\n<ckc>base64encoded</ckc>\n'
    // so trim the excess:
    let keyText = request.responseText.trim();
    if (keyText.substr(0, 5) === '<ckc>' && keyText.substr(-6) === '</ckc>') {
      keyText = keyText.slice(5, -6);
    }
    const key = FW.#base64DecodeUint8Array(keyText);
    Logger.print('fps', 'license key', key);
    session.update(key);
  }

  static hexToRgb(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      };
    } else {
      return null;
    }
  }

  static eventStopPrevent(event) {
    if (event) {
      event.stopPropagation();
      if (event.type === 'touchend') {
        event.preventDefault();
      }
    }
  }

  static addEvents(events, domElement, callback) {
    if (events && events.length > 1 && domElement && typeof callback === 'function') {
      events.forEach(event => {
        domElement.addEventListener(event, callback);
      });
    }
  }

  static addEventsOnce(events, domElement, callback) {
    if (events && events.length > 1 && domElement && typeof callback === 'function') {
      events.forEach(event => {
        domElement.addEventListener(event, callback, { once: true });
      });
    }
  }

  static removeEvents(events, domElement, callback) {
    if (events && events.length > 1 && domElement && typeof callback === 'function') {
      events.forEach(event => {
        domElement.removeEventListener(event, callback);
      });
    }
  }

  static getFQURL(base, relative) {
    const stack = base.split('/');
    const parts = relative.split('/');
    stack.pop(); // remove current file name (or empty string)
    // (omit if "base" is the current folder without trailing slash)
    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === '.') {
        continue;
      }
      if (parts[i] === '..') {
        stack.pop();
      } else {
        stack.push(parts[i]);
      }
    }
    return stack.join('/');
  }

  static proxyFullscreenEvents(event) {
    if (event && event.type) {
      const newType = event.type.replace(/^(webkit|moz|MS)/, '').toLowerCase();
      FW.createSyntheticEvent(newType, document);
    }
  }

  static isValidCCFiles(ccFiles) {
    if (Array.isArray(ccFiles) && ccFiles[0] && ccFiles[0].lng && ccFiles[0].name && ccFiles[0].uri) {
      return true;
    }
    return false;
  }

  static preventKeyDown(event) {
    if (event && typeof event.key === 'string' && event.key !== '' && event.target) {
      // do not preventKeyDown for c/f/m to allow copy/paste
      const tagName = event.target.tagName.toLowerCase();
      switch (event.key) {
        case ' ':
        case 'ArrowRight':
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'ArrowDown':
        case 'Escape':
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
        case 'Enter':
          if (tagName !== 'select' && tagName !== 'a') {
            event.preventDefault();
          }
          break;
        default:
          break;
      }
    }
  }

  static insertBefore(parent, newNode, referenceNode) {
    if (parent.contains(referenceNode)) {
      parent.insertBefore(newNode, referenceNode);
    } else {
      console.warn(`insertBefore called but input referenceNode is not part of parent`);
    }
  }

  static mapLanguageCodeToName(lngCode) {
    let result = '';
    const code = lngCode.split('-')[0];
    const keys = Object.keys(Language.codes);
    for (let i = 0, len = keys.length; i < len; i++) {
      const key = keys[i];
      if (Language.codes[key].includes(code)) {
        result = key;
        break;
      }
    }
    if (result) {
      return result;
    } else {
      return lngCode;
    }
  }
}
